import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, MapPin, Clock, Sparkles, Check, X, ArrowRight, ChevronDown, Calendar, Home, Users, Bed, Bath, AlertTriangle, Star, FileText, Info, ChevronLeft, ChevronRight, Zap, TrendingUp, Route } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
// Mock Data Types
type RequestStatus = 'new' | 'accepted' | 'rejected';
interface PropertyDetails {
  address: string;
  type: string;
  size: string;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  amenities: string[];
  desiderata: string[];
  attentionPoints: string[];
  hostNotes: string;
}
interface Request {
  id: string;
  date: string;
  zone: string;
  serviceType: string;
  timeWindow: string;
  duration: string;
  propertyType: 'Appartamento' | 'Villa' | 'Loft' | 'Ufficio';
  credits: number;
  match?: number;
  matchReason?: string;
  status: RequestStatus;
  refusedDate?: string;
  scheduledTime?: string;
  property?: PropertyDetails;
}
// Mock Data with property details
const MOCK_REQUESTS: Request[] = [{
  id: '#REQ-2024-0156',
  date: 'Lun 22 Gen 2024',
  zone: 'Milano - Porta Venezia',
  serviceType: 'Pulizia standard',
  timeWindow: '10:00 - 15:00',
  duration: '~3 ore',
  propertyType: 'Appartamento',
  credits: 15,
  match: 92,
  matchReason: 'Zona già servita',
  status: 'new',
  property: {
    address: 'Via Lecco 15, Milano',
    type: 'Appartamento',
    size: '75 mq',
    bedrooms: 2,
    bathrooms: 1,
    maxGuests: 4,
    amenities: ['Lavatrice', 'Lavastoviglie', 'Balcone', 'Aria condizionata'],
    desiderata: ['Usare solo prodotti eco-friendly', 'Piegare gli asciugamani a cigno', 'Lasciare finestre aperte per 10 min'],
    attentionPoints: ['Parquet delicato - non usare troppa acqua', 'Gatto presente - attenzione alle porte', 'Piante sul balcone da innaffiare'],
    hostNotes: 'Ospiti arrivano alle 15:00, check-out alle 10:00. Chiavi nella cassetta codice 1234.'
  }
}, {
  id: '#REQ-2024-0157',
  date: 'Mar 23 Gen 2024',
  zone: 'Milano - Navigli',
  serviceType: 'Pulizia profonda',
  timeWindow: '11:00 - 16:00',
  duration: '~4 ore',
  propertyType: 'Loft',
  credits: 25,
  match: 88,
  matchReason: 'Ottimizzazione percorso',
  status: 'new',
  property: {
    address: 'Ripa di Porta Ticinese 55, Milano',
    type: 'Loft',
    size: '120 mq',
    bedrooms: 1,
    bathrooms: 2,
    maxGuests: 2,
    amenities: ['Lavatrice', 'Lavastoviglie', 'Terrazzo', 'Smart TV'],
    desiderata: ['Pulizia vetrate panoramiche', 'Cambio biancheria completo', 'Rifornire minibar'],
    attentionPoints: ['Scala interna ripida', "Opere d'arte - non toccare", 'Sistema domotico - non modificare'],
    hostNotes: 'Loft di design, ospiti VIP. Massima cura nei dettagli.'
  }
}, {
  id: '#REQ-2024-0158',
  date: 'Mer 24 Gen 2024',
  zone: 'Milano - Centro Storico',
  serviceType: 'Pulizia standard',
  timeWindow: '09:00 - 14:00',
  duration: '~5 ore',
  propertyType: 'Villa',
  credits: 50,
  match: 75,
  status: 'new',
  property: {
    address: 'Via Montenapoleone 8, Milano',
    type: 'Villa',
    size: '250 mq',
    bedrooms: 4,
    bathrooms: 3,
    maxGuests: 8,
    amenities: ['Piscina', 'Giardino', 'Garage', 'Sauna'],
    desiderata: ['Pulizia piscina inclusa', 'Cura del giardino', 'Preparare welcome kit'],
    attentionPoints: ['Allarme - codice fornito separatamente', 'Cane di grossa taglia', 'Mobili antichi - prodotti specifici'],
    hostNotes: 'Villa storica, trattare con estrema cura. Contattare prima di entrare.'
  }
}, {
  id: '#REQ-2024-0159',
  date: 'Gio 25 Gen 2024',
  zone: 'Milano - Isola',
  serviceType: 'Pulizia standard',
  timeWindow: '10:30 - 15:30',
  duration: '~2.5 ore',
  propertyType: 'Appartamento',
  credits: 12,
  match: 95,
  matchReason: 'Zona abituale',
  status: 'new',
  property: {
    address: 'Via Volturno 33, Milano',
    type: 'Appartamento',
    size: '55 mq',
    bedrooms: 1,
    bathrooms: 1,
    maxGuests: 2,
    amenities: ['Lavatrice', 'Microonde', 'WiFi veloce'],
    desiderata: ['Lasciare luci soffuse', 'Preparare caffè nella moka'],
    attentionPoints: ['Vicini sensibili al rumore', 'Non usare aspirapolvere prima delle 9'],
    hostNotes: 'Appartamento semplice ma curato. Ospiti business.'
  }
}, {
  id: '#REQ-2024-0160',
  date: 'Ven 26 Gen 2024',
  zone: 'Milano - Lambrate',
  serviceType: 'Pulizia post-ristrutturazione',
  timeWindow: '08:00 - 18:00',
  duration: '~6 ore',
  propertyType: 'Ufficio',
  credits: 30,
  status: 'new',
  property: {
    address: 'Via Rombon 78, Milano',
    type: 'Ufficio',
    size: '150 mq',
    bedrooms: 0,
    bathrooms: 2,
    maxGuests: 0,
    amenities: ['Open space', 'Sala riunioni', 'Cucina'],
    desiderata: ['Rimuovere polvere da ristrutturazione', 'Pulire tutte le superfici', 'Lavare pavimenti 2 volte'],
    attentionPoints: ['Materiale edile ancora presente', 'Usare DPI appropriati', 'Verificare prese elettriche'],
    hostNotes: "Ufficio appena ristrutturato. Pulizia profonda necessaria prima dell'inaugurazione."
  }
}, {
  id: '#REQ-2024-0150',
  date: 'Ven 19 Gen 2024',
  zone: 'Milano - Centro',
  serviceType: 'Pulizia standard',
  timeWindow: '11:00 - 15:00',
  duration: '~3 ore',
  propertyType: 'Appartamento',
  credits: 15,
  status: 'accepted',
  scheduledTime: '11:00 - 14:00',
  property: {
    address: 'Corso Vittorio Emanuele 20, Milano',
    type: 'Appartamento',
    size: '80 mq',
    bedrooms: 2,
    bathrooms: 1,
    maxGuests: 4,
    amenities: ['Lavatrice', 'Lavastoviglie', 'Balcone'],
    desiderata: ['Cambio lenzuola', 'Rifornire saponi'],
    attentionPoints: ['Citofono non funziona - chiamare'],
    hostNotes: 'Appartamento centrale, facile accesso.'
  }
}, {
  id: '#REQ-2024-0145',
  date: 'Lun 15 Gen 2024',
  zone: 'Monza',
  serviceType: 'Pulizia standard',
  timeWindow: '09:00 - 12:00',
  duration: '~3 ore',
  propertyType: 'Appartamento',
  credits: 15,
  status: 'rejected',
  refusedDate: '14 Gen 2024'
}];
const ITEMS_PER_PAGE = 3;
// Helper function to get detailed match explanation
const getMatchExplanation = (reason?: string, match?: number) => {
  const explanations: Record<string, {
    title: string;
    description: string;
    benefits: string[];
  }> = {
    'Zona già servita': {
      title: 'Zona già servita',
      description: "Questa proprietà si trova in una zona dove hai già completato interventi con successo. Il nostro algoritmo AI ha rilevato che conosci bene l'area e le sue caratteristiche.",
      benefits: ['Conosci già i tempi di percorrenza nella zona', "Hai familiarità con le tipologie di immobili dell'area", 'Minore stress logistico per raggiungere la destinazione', 'Possibilità di ottimizzare altri interventi nella stessa zona']
    },
    'Zona abituale': {
      title: 'Zona abituale',
      description: "Questa è una delle tue zone di lavoro preferite. L'AI ha analizzato il tuo storico e ha identificato che operi frequentemente in quest'area con ottimi risultati.",
      benefits: ['Alta familiarità con la zona e i suoi servizi', 'Ottimo track record di interventi completati', 'Clienti soddisfatti nella stessa area', 'Massima efficienza operativa']
    },
    'Ottimizzazione percorso': {
      title: 'Ottimizzazione percorso',
      description: "Questo intervento è stato suggerito perché si inserisce perfettamente nel tuo percorso giornaliero. L'AI ha calcolato che puoi raggiungerlo facilmente tra altri impegni.",
      benefits: ['Riduzione dei tempi di spostamento', 'Risparmio sui costi di trasporto', 'Migliore gestione del tempo', 'Possibilità di accettare più interventi nella giornata']
    }
  };
  return explanations[reason || ''] || {
    title: 'Match perfetto',
    description: `Questo intervento ha un punteggio di compatibilità del ${match}%. L'algoritmo AI ha analizzato diversi fattori per determinare che questa richiesta è particolarmente adatta al tuo profilo.`,
    benefits: ['Alta compatibilità con il tuo profilo', "Condizioni favorevoli per l'intervento", 'Buona probabilità di soddisfazione reciproca', 'Ottimizzazione delle tue risorse']
  };
};
export function RichiestePage() {
  const [activeTab, setActiveTab] = useState<'nuove' | 'accettate' | 'rifiutate'>('nuove');
  const [userCredits] = useState(42);
  const [aiFilterEnabled, setAiFilterEnabled] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [matchModalRequest, setMatchModalRequest] = useState<Request | null>(null);
  const filteredRequests = MOCK_REQUESTS.filter(req => {
    if (req.status !== (activeTab === 'nuove' ? 'new' : activeTab === 'accettate' ? 'accepted' : 'rejected')) {
      return false;
    }
    if (activeTab === 'nuove' && aiFilterEnabled && !req.match) {
      return false;
    }
    return true;
  });
  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRequests = filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  // Reset to page 1 when changing tabs or filters
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, aiFilterEnabled]);
  const counts = {
    nuove: 5,
    accettate: 8,
    rifiutate: 2
  };
  const handleCardClick = (req: Request) => {
    setSelectedRequest(req);
  };
  const closeModal = () => {
    setSelectedRequest(null);
  };
  const handleMatchClick = (e: React.MouseEvent, req: Request) => {
    e.stopPropagation();
    setMatchModalRequest(req);
  };
  const closeMatchModal = () => {
    setMatchModalRequest(null);
  };
  return <MobileLayout data-id="element-905">
      {/* Page Header */}
      <div className="mb-6" data-id="element-906">
        <h1 className="text-2xl font-bold text-navy font-montserrat" data-id="element-907">
          Richieste
        </h1>
        <p className="text-gray-500 text-sm mt-1" data-id="element-908">
          Gestisci le richieste di intervento da CleanAI
        </p>
      </div>

      {/* Tab Navigation - Underline style */}
      <div className="border-b border-gray-200 mb-6" data-id="element-909">
        <div className="flex gap-6 -mb-px overflow-x-auto scrollbar-hide" data-id="element-910">
          {(['nuove', 'accettate', 'rifiutate'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`
                pb-3 text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 border-b-2
                ${activeTab === tab ? 'border-teal-deep text-teal-deep' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `} data-id="element-911">
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className={`
                  text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center
                  ${activeTab === tab ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}
                `} data-id="element-912">
                {counts[tab]}
              </span>
            </button>)}
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6" data-id="element-913">
        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-914">
          <div className="relative flex-1 sm:flex-none" data-id="element-915">
            <select className="w-full sm:w-40 appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20 pr-8 cursor-pointer" data-id="element-916">
              <option data-id="element-917">Tutte le zone</option>
              <option data-id="element-918">Milano Centro</option>
              <option data-id="element-919">Milano Navigli</option>
              <option data-id="element-920">Milano Porta Venezia</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-921" />
          </div>

          <div className="relative flex-1 sm:flex-none" data-id="element-922">
            <select className="w-full sm:w-40 appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20 pr-8 cursor-pointer" data-id="element-923">
              <option data-id="element-924">Costo crediti</option>
              <option data-id="element-925">1-10 crediti</option>
              <option data-id="element-926">11-20 crediti</option>
              <option data-id="element-927">21+ crediti</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-928" />
          </div>
        </div>

        <div className="flex items-center gap-3 ml-auto" data-id="element-929">
          <span className="text-sm text-gray-600 flex items-center gap-1.5" data-id="element-930">
            <Sparkles size={14} className="text-teal-500" data-id="element-931" />
            Solo suggerimenti AI
          </span>
          <button onClick={() => setAiFilterEnabled(!aiFilterEnabled)} className={`w-11 h-6 rounded-full transition-colors relative ${aiFilterEnabled ? 'bg-teal-500' : 'bg-gray-200'}`} data-id="element-932">
            <span className={`
              absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transition-transform
              ${aiFilterEnabled ? 'translate-x-5' : 'translate-x-0'}
            `} data-id="element-933" />
          </button>
        </div>
      </div>

      {/* Request Cards - Clickable */}
      <div className="space-y-3" data-id="element-934">
        {paginatedRequests.map(req => <div key={req.id} onClick={() => handleCardClick(req)} className={`
              bg-white border border-gray-200 rounded-xl p-5 transition-all hover:shadow-sm cursor-pointer
              ${req.status === 'rejected' ? 'opacity-60' : ''}
            `} data-id="element-935">
            <div className="flex flex-col gap-4" data-id="element-936">
              {/* Top Row - Zone & Match Badge */}
              <div className="flex items-start justify-between gap-4" data-id="element-937">
                {/* Left Column - Details */}
                <div className="flex-1 min-w-0" data-id="element-938">
                  {/* Zone */}
                  <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-939">
                    <h3 className="text-base font-semibold text-navy truncate" data-id="element-940">
                      {req.zone}
                    </h3>
                    {req.status === 'accepted' && <span className="text-xs font-medium text-green-600" data-id="element-941">
                        Confermata
                      </span>}
                    {req.status === 'rejected' && <span className="text-xs font-medium text-gray-400" data-id="element-942">
                        Rifiutata
                      </span>}
                  </div>

                  {/* Date and Time */}
                  <p className="text-sm text-gray-500 mb-3" data-id="element-943">
                    {req.date} ·{' '}
                    {req.status === 'accepted' ? req.scheduledTime : req.timeWindow}
                    {req.duration && <span className="text-gray-400" data-id="element-944"> · {req.duration}</span>}
                  </p>

                  {/* Service Type Tag */}
                  <span className="inline-block text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-md" data-id="element-945">
                    {req.serviceType}
                  </span>
                </div>

                {/* Right Column - Match Badge (top right) */}
                {req.match && req.match >= 90 && req.status === 'new' && <button onClick={e => handleMatchClick(e, req)} className="inline-flex items-center gap-1 text-xs font-medium text-teal-deep bg-white border border-teal-deep px-2 py-1 rounded-full whitespace-nowrap hover:bg-teal-50 transition-colors flex-shrink-0" data-id="element-946">
                    <Sparkles size={10} className="text-teal-deep" data-id="element-947" />
                    Match perfetto
                  </button>}
              </div>

              {/* Bottom Row - Credits (left) + Actions (right) */}
              {req.status === 'new' && <div className="flex items-center justify-between" data-id="element-948">
                  {/* Left: Credits */}
                  <div className="flex items-baseline gap-1" data-id="element-949">
                    <span className="text-2xl font-bold text-navy" data-id="element-950">
                      {req.credits}
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-951">crediti</span>
                  </div>

                  {/* Right: Action Buttons */}
                  <div className="flex gap-2" data-id="element-952">
                    <button onClick={e => {
                e.stopPropagation();
              }} className="flex items-center justify-center gap-1.5 px-4 py-2 text-gray-500 text-sm rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-953">
                      <X size={14} data-id="element-954" />
                      Rifiuta
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
              }} disabled={userCredits < req.credits} className="flex items-center justify-center gap-1.5 px-4 py-2 bg-teal-deep text-white text-sm rounded-lg font-medium hover:bg-teal-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-955">
                      <Check size={14} data-id="element-956" />
                      Accetta
                    </button>
                  </div>
                </div>}

              {req.status === 'accepted' && <div className="flex items-center justify-between" data-id="element-957">
                  <div className="flex items-baseline gap-1" data-id="element-958">
                    <span className="text-2xl font-bold text-navy" data-id="element-959">
                      {req.credits}
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-960">crediti</span>
                  </div>
                  <button onClick={e => {
              e.stopPropagation();
            }} className="flex items-center gap-1.5 text-sm text-teal-deep font-medium hover:text-teal-800 transition-colors" data-id="element-961">
                    Gestisci
                    <ArrowRight size={14} data-id="element-962" />
                  </button>
                </div>}

              {req.status === 'rejected' && <div className="flex items-center justify-between" data-id="element-963">
                  <div className="flex items-baseline gap-1" data-id="element-964">
                    <span className="text-2xl font-bold text-navy" data-id="element-965">
                      {req.credits}
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-966">crediti</span>
                  </div>
                  {req.refusedDate && <span className="text-xs text-gray-400" data-id="element-967">
                      {req.refusedDate}
                    </span>}
                </div>}
            </div>
          </div>)}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100" data-id="element-968">
          <div className="text-sm text-gray-500" data-id="element-969">
            {startIndex + 1}-
            {Math.min(startIndex + ITEMS_PER_PAGE, filteredRequests.length)} di{' '}
            {filteredRequests.length}
          </div>
          <div className="flex items-center gap-2" data-id="element-970">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-971">
              <ChevronLeft size={16} data-id="element-972" />
            </button>
            <div className="flex items-center gap-1" data-id="element-973">
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-teal-deep text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-974">
                  {page}
                </button>)}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-975">
              <ChevronRight size={16} data-id="element-976" />
            </button>
          </div>
        </div>}

      {/* Match Explanation Modal */}
      {matchModalRequest && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={closeMatchModal} data-id="element-977">
          <div className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()} data-id="element-978">
            {/* Modal Header */}
            <div className="px-[30px] py-4 border-b border-gray-100 bg-white" data-id="element-979">
              <div className="flex items-center justify-between" data-id="element-980">
                <div className="flex items-center gap-3" data-id="element-981">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center" data-id="element-982">
                    <Sparkles size={20} className="text-teal-deep" data-id="element-983" />
                  </div>
                  <div data-id="element-984">
                    <h2 className="text-lg font-bold text-navy" data-id="element-985">
                      Match Perfetto
                    </h2>
                    <p className="text-xs text-gray-500" data-id="element-986">
                      Suggerimento AI per te
                    </p>
                  </div>
                </div>
                <button onClick={closeMatchModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-987">
                  <X size={20} className="text-gray-500" data-id="element-988" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-[30px] py-4 space-y-4" data-id="element-989">
              {/* Match Score */}
              <div className="text-center" data-id="element-990">
                <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-teal-100 flex items-center justify-center" data-id="element-991">
                  <Sparkles size={40} className="text-teal-deep" data-id="element-992" />
                </div>
                <h3 className="text-2xl font-bold text-navy mb-1" data-id="element-993">
                  {matchModalRequest.match}%
                </h3>
                <p className="text-sm text-gray-500" data-id="element-994">
                  {matchModalRequest.matchReason || 'Match perfetto'}
                </p>
              </div>

              {/* Explanation */}
              {(() => {
            const explanation = getMatchExplanation(matchModalRequest.matchReason, matchModalRequest.match);
            return <>
                    <div data-id="element-995">
                      <div className="mb-2" data-id="element-996">
                        <h3 className="font-semibold text-navy" data-id="element-997">
                          {explanation.title}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-998">
                        {explanation.description}
                      </p>
                    </div>

                    {/* Benefits */}
                    <div data-id="element-999">
                      <div className="mb-3" data-id="element-1000">
                        <h3 className="font-semibold text-navy" data-id="element-1001">
                          Vantaggi per te
                        </h3>
                      </div>
                      <ul className="space-y-2" data-id="element-1002">
                        {explanation.benefits.map((benefit, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-600" data-id="element-1003">
                            <Check size={14} className="text-teal-deep mt-0.5 flex-shrink-0" data-id="element-1004" />
                            {benefit}
                          </li>)}
                      </ul>
                    </div>
                  </>;
          })()}

              {/* Request Summary */}
              <div className="bg-gray-50 rounded-xl p-4" data-id="element-1005">
                <div className="flex items-center gap-2 mb-3" data-id="element-1006">
                  <MapPin size={14} className="text-gray-400" data-id="element-1007" />
                  <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-1008">
                    Dettagli richiesta
                  </span>
                </div>
                <div className="space-y-2 text-sm" data-id="element-1009">
                  <div className="flex justify-between" data-id="element-1010">
                    <span className="text-gray-500" data-id="element-1011">Zona</span>
                    <span className="font-medium text-navy" data-id="element-1012">
                      {matchModalRequest.zone}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1013">
                    <span className="text-gray-500" data-id="element-1014">Data</span>
                    <span className="font-medium text-navy" data-id="element-1015">
                      {matchModalRequest.date}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1016">
                    <span className="text-gray-500" data-id="element-1017">Servizio</span>
                    <span className="font-medium text-navy" data-id="element-1018">
                      {matchModalRequest.serviceType}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1019">
                    <span className="text-gray-500" data-id="element-1020">Crediti</span>
                    <span className="font-bold text-teal-deep" data-id="element-1021">
                      {matchModalRequest.credits} crediti
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-[30px] py-4 border-t border-gray-100 bg-white" data-id="element-1022">
              <button onClick={closeMatchModal} className="w-full px-4 py-3 bg-teal-deep text-white text-sm rounded-lg font-medium hover:bg-teal-800 transition-colors" data-id="element-1023">
                Ho capito
              </button>
            </div>
          </div>
        </div>}

      {/* Request Detail Modal */}
      {selectedRequest && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={closeModal} data-id="element-1024">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()} data-id="element-1025">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-1026">
              <div data-id="element-1027">
                <h2 className="text-lg font-bold text-navy" data-id="element-1028">
                  {selectedRequest.zone}
                </h2>
                <p className="text-xs text-gray-500" data-id="element-1029">{selectedRequest.id}</p>
              </div>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1030">
                <X size={20} className="text-gray-500" data-id="element-1031" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-1032">
              {/* Service Info */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1033">
                <div className="flex items-center justify-between mb-3" data-id="element-1034">
                  <div className="flex items-center gap-2" data-id="element-1035">
                    <Calendar size={14} className="text-gray-400" data-id="element-1036" />
                    <span className="text-sm font-medium text-navy" data-id="element-1037">
                      {selectedRequest.date}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500" data-id="element-1038">
                    {selectedRequest.timeWindow}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1039">
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded" data-id="element-1040">
                    {selectedRequest.serviceType}
                  </span>
                  <div className="text-right" data-id="element-1041">
                    <span className="text-xl font-bold text-navy" data-id="element-1042">
                      {selectedRequest.credits}
                    </span>
                    <span className="text-sm text-gray-400 ml-1" data-id="element-1043">crediti</span>
                  </div>
                </div>
              </div>

              {selectedRequest.property && <>
                  {/* Property Details */}
                  <div data-id="element-1044">
                    <div className="flex items-center gap-2 mb-3" data-id="element-1045">
                      <Home size={14} className="text-gray-400" data-id="element-1046" />
                      <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-1047">
                        Dettagli proprietà
                      </span>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-1048">
                      <div className="flex items-start gap-2 mb-3" data-id="element-1049">
                        <MapPin size={14} className="text-teal-600 mt-0.5" data-id="element-1050" />
                        <span className="text-sm text-navy" data-id="element-1051">
                          {selectedRequest.property.address}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-1052">
                        <div className="flex items-center gap-2" data-id="element-1053">
                          <Home size={14} className="text-gray-400" data-id="element-1054" />
                          <span className="text-gray-600" data-id="element-1055">
                            {selectedRequest.property.type} ·{' '}
                            {selectedRequest.property.size}
                          </span>
                        </div>
                        {selectedRequest.property.bedrooms > 0 && <div className="flex items-center gap-2" data-id="element-1056">
                            <Bed size={14} className="text-gray-400" data-id="element-1057" />
                            <span className="text-gray-600" data-id="element-1058">
                              {selectedRequest.property.bedrooms} camere
                            </span>
                          </div>}
                        <div className="flex items-center gap-2" data-id="element-1059">
                          <Bath size={14} className="text-gray-400" data-id="element-1060" />
                          <span className="text-gray-600" data-id="element-1061">
                            {selectedRequest.property.bathrooms} bagni
                          </span>
                        </div>
                        {selectedRequest.property.maxGuests > 0 && <div className="flex items-center gap-2" data-id="element-1062">
                            <Users size={14} className="text-gray-400" data-id="element-1063" />
                            <span className="text-gray-600" data-id="element-1064">
                              Max {selectedRequest.property.maxGuests} ospiti
                            </span>
                          </div>}
                      </div>
                      {selectedRequest.property.amenities.length > 0 && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-1065">
                          <div className="flex flex-wrap gap-1.5" data-id="element-1066">
                            {selectedRequest.property.amenities.map((amenity, i) => <span key={i} className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-1067">
                                  {amenity}
                                </span>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  {/* Desiderata */}
                  {selectedRequest.property.desiderata.length > 0 && <div data-id="element-1068">
                      <div className="flex items-center gap-2 mb-3" data-id="element-1069">
                        <Star size={14} className="text-gray-400" data-id="element-1070" />
                        <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-1071">
                          Desiderata
                        </span>
                      </div>
                      <div className="bg-teal-50 border border-teal-100 rounded-lg p-4" data-id="element-1072">
                        <ul className="space-y-2" data-id="element-1073">
                          {selectedRequest.property.desiderata.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-teal-800" data-id="element-1074">
                                <Check size={14} className="text-teal-600 mt-0.5 flex-shrink-0" data-id="element-1075" />
                                {item}
                              </li>)}
                        </ul>
                      </div>
                    </div>}

                  {/* Attention Points */}
                  {selectedRequest.property.attentionPoints.length > 0 && <div data-id="element-1076">
                      <div className="flex items-center gap-2 mb-3" data-id="element-1077">
                        <AlertTriangle size={14} className="text-gray-400" data-id="element-1078" />
                        <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-1079">
                          Punti di attenzione
                        </span>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-4" data-id="element-1080">
                        <ul className="space-y-2" data-id="element-1081">
                          {selectedRequest.property.attentionPoints.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-amber-800" data-id="element-1082">
                                <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" data-id="element-1083" />
                                {item}
                              </li>)}
                        </ul>
                      </div>
                    </div>}

                  {/* Host Notes */}
                  {selectedRequest.property.hostNotes && <div data-id="element-1084">
                      <div className="flex items-center gap-2 mb-3" data-id="element-1085">
                        <FileText size={14} className="text-gray-400" data-id="element-1086" />
                        <span className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-1087">
                          Note dell'host
                        </span>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1088">
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1089">
                          {selectedRequest.property.hostNotes}
                        </p>
                      </div>
                    </div>}
                </>}
            </div>

            {/* Modal Footer - Actions */}
            <div className="p-4 border-t border-gray-100 bg-white" data-id="element-1090">
              {selectedRequest.status === 'new' && <div className="flex gap-3" data-id="element-1091">
                  <button onClick={closeModal} className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 text-gray-600 text-sm rounded-lg font-medium border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-1092">
                    <X size={16} data-id="element-1093" />
                    Rifiuta
                  </button>
                  <button disabled={userCredits < selectedRequest.credits} className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 bg-teal-deep text-white text-sm rounded-lg font-medium hover:bg-teal-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1094">
                    <Check size={16} data-id="element-1095" />
                    Accetta ({selectedRequest.credits} cr)
                  </button>
                </div>}
              {selectedRequest.status === 'accepted' && <button onClick={closeModal} className="w-full px-4 py-3 bg-gray-100 text-navy text-sm rounded-lg font-medium hover:bg-gray-200 transition-colors" data-id="element-1096">
                  Chiudi
                </button>}
              {selectedRequest.status === 'rejected' && <button onClick={closeModal} className="w-full px-4 py-3 bg-gray-100 text-navy text-sm rounded-lg font-medium hover:bg-gray-200 transition-colors" data-id="element-1097">
                  Chiudi
                </button>}
            </div>
          </div>
        </div>}
    </MobileLayout>;
}